/** Shared resolver credit + gating helpers for SPA feature buttons. */

import { toast } from 'react-toastify'
import { formatEstimateCents } from './creditAffordabilityClient'
import { getResolverLoginWarning } from './mediaProxyClient'
import {
  OFFLINE_MESSAGE,
  getOfflineBlock,
  isNavigatorOffline,
} from './offlineNetwork'

export function normalizeAccessToken(token) {
  if (!token) return null
  if (typeof token === 'string') return token
  return token.access_token || null
}

export function getCreditBalanceCents(resolverStatus) {
  if (!resolverStatus || resolverStatus.creditBalanceCents == null) return null
  const value = Number(resolverStatus.creditBalanceCents)
  return Number.isFinite(value) ? value : null
}

export function resolverBillingEnabled(resolverStatus) {
  return !!(resolverStatus && resolverStatus.billingEnabled)
}

export function getGatedActionLabel(access, actionLabel) {
  const label = actionLabel || ''
  if (!access) return label
  if (access.needsNetwork) return label
  if (access.needsLogin) return 'Login to ' + label
  if (access.needsCredit) return 'Buy Credit to ' + label
  if (access.cannotAfford) {
    const est = formatEstimateCents(access.estimateCents)
    if (est) return 'Insufficient credit (~' + est + ' needed) — ' + label
    return 'Insufficient credit — ' + label
  }
  return label
}

export function mergeAffordanceIntoAccess(baseAccess, affordance) {
  const base = baseAccess || {}
  const afford = affordance || {}
  if (!afford.checked || afford.creditUnlimited || afford.error) {
    return base
  }
  const cannotAfford = !afford.affordable
  const needsCredit = base.needsCredit || cannotAfford
  const canUse = base.canUse && !cannotAfford
  const canGenerate = base.canGenerate != null ? (base.canGenerate && !cannotAfford) : undefined
  return Object.assign({}, base, {
    cannotAfford: cannotAfford,
    needsCredit: needsCredit,
    canUse: canUse,
    canGenerate: canGenerate != null ? canGenerate : canUse,
    estimateCents: afford.estimateCents,
    availableCents: afford.availableCents,
    shortfallCents: afford.shortfallCents,
    affordanceChecked: afford.checked,
  })
}

export function getResolverGatedActionAccess(context, options) {
  const opts = context || {}
  const gate = options || {}
  const requiresFeature = gate.requiresFeature || null
  const resolverAvailable = !!opts.resolverAvailable
  const resolverChecked = !!opts.resolverChecked
  const features = opts.features || {}
  const hasFeature = !requiresFeature || !!features[requiresFeature]
  const offlineBlock = getOfflineBlock()
  const needsNetwork = !!offlineBlock
  const loginWarning = needsNetwork
    ? offlineBlock
    : getResolverLoginWarning(opts.resolverStatus, normalizeAccessToken(opts.accessToken))
  const needsLogin = !needsNetwork && !!(loginWarning && loginWarning.showLoginButton)
  const needsCredit = !needsNetwork && !!(loginWarning && loginWarning.showBuyCreditButton)
  const hasCapability = resolverAvailable && hasFeature && !needsNetwork
  const showButton = resolverChecked && (hasCapability || needsLogin || needsCredit)

  return {
    showButton: showButton,
    needsLogin: needsLogin && showButton,
    needsCredit: needsCredit && showButton,
    needsNetwork: needsNetwork,
    canUse: hasCapability && !needsLogin && !needsCredit,
    loginWarning: loginWarning,
  }
}

export function openCreditSettings() {
  if (typeof window === 'undefined') return
  const hashPath = '#/settings/providers?credit=1'
  const hash = String(window.location.hash || '')
  if (hash.indexOf('#/settings') === 0) {
    if (hash.indexOf('#/settings/providers') === 0) {
      window.dispatchEvent(new CustomEvent('tunebook-open-credit-settings'))
      return
    }
    window.location.hash = hashPath
    window.dispatchEvent(new CustomEvent('tunebook-open-credit-settings'))
    return
  }
  window.location.assign('/' + hashPath)
}

export function runResolverGatedAction(access, handlers) {
  const opts = handlers || {}
  if (!access) return false
  if (access.needsNetwork || isNavigatorOffline()) {
    if (typeof opts.onOffline === 'function') {
      opts.onOffline()
    } else {
      toast.info(OFFLINE_MESSAGE)
    }
    return true
  }
  if (!access.showButton && !access.showOption) return false
  if (access.needsLogin) {
    if (typeof opts.login === 'function') {
      opts.login()
    }
    return true
  }
  if (access.needsCredit) {
    if (typeof opts.buyCredit === 'function') {
      opts.buyCredit()
    } else {
      openCreditSettings()
    }
    return true
  }
  if (access.cannotAfford) {
    if (typeof opts.buyCredit === 'function') {
      opts.buyCredit()
    } else {
      openCreditSettings()
    }
    return true
  }
  if (typeof opts.onReady === 'function') {
    opts.onReady()
  }
  return false
}
